import React, { useCallback, useMemo, useState } from 'react';
import { StyledContainer } from '../../../components/container/styled';
import { TextLarge } from '../../../components/text';
import { CardTheme } from '../../../components/card';
import { Button, Col, Row, Spin } from 'antd';
import { ButtonTheme, RenderForm } from '../../../components';
import {
  FORM_USER_REGISTRATION_WARRANTY,
  FORM_WARRANTY_ADD_PRODUCT_LEADING,
  FORM_WARRANTY_ADD_PRODUCT_TRAILING,
  getWarrantyAddBrandModelFields,
  getWarrantyAddBrandModelFieldNames,
} from './form';
import { useGetWarrantyAddList } from './hooks/useGetWarrantyAddList';
import { ContainerButton } from '../../../styles/global-style';

const MAX_EXTRA_BRAND_SETS = 2;

export const WarrantyAdd = () => {
  const [extraBrandSetCount, setExtraBrandSetCount] = useState(0);

  const handleWarrantyAddSuccess = useCallback(() => {
    setExtraBrandSetCount(0);
  }, []);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    handleOnEnterTele,
    handleOnEnterBarcode,
    onSubmit,
    isSubmitting,
    isloadingWarrantyAdd,
    isloadingBarcode,
    isloadingTele,
    channelBuyOptions,
    mobileBrandOptions,
  } = useGetWarrantyAddList({ onWarrantyAddSuccess: handleWarrantyAddSuccess });

  const productLeadingForms = useMemo(
    () =>
      FORM_WARRANTY_ADD_PRODUCT_LEADING({
        onEnter: handleOnEnterBarcode,
        mobileBrandOptions,
      }),
    [handleOnEnterBarcode, mobileBrandOptions],
  );

  const productTrailingForms = useMemo(
    () => FORM_WARRANTY_ADD_PRODUCT_TRAILING({ channelBuyOptions }),
    [channelBuyOptions],
  );

  const extraBrandFormBlocks = useMemo(() => {
    const blocks = [];
    for (let i = 1; i <= extraBrandSetCount; i += 1) {
      blocks.push(getWarrantyAddBrandModelFields(i, mobileBrandOptions));
    }
    return blocks;
  }, [extraBrandSetCount, mobileBrandOptions]);

  const clearBrandModelSet = useCallback(
    (setIndex) => {
      const { brand, model } = getWarrantyAddBrandModelFieldNames(setIndex);
      setValue(brand, undefined, { shouldDirty: true });
      setValue(model, '', { shouldDirty: true });
    },
    [setValue],
  );

  const handleRemoveExtraBrandBlock = useCallback(
    (blockIndex) => {
      const c = extraBrandSetCount;
      if (c < 1 || blockIndex < 0 || blockIndex >= c) return;
      for (let s = blockIndex + 1; s < c; s += 1) {
        const from = getWarrantyAddBrandModelFieldNames(s + 1);
        const to = getWarrantyAddBrandModelFieldNames(s);
        setValue(to.brand, getValues(from.brand), { shouldDirty: true });
        setValue(to.model, getValues(from.model) ?? '', { shouldDirty: true });
      }
      clearBrandModelSet(c);
      setExtraBrandSetCount((n) => n - 1);
    },
    [extraBrandSetCount, getValues, setValue, clearBrandModelSet],
  );

  const canAddMoreBrandSets = extraBrandSetCount < MAX_EXTRA_BRAND_SETS;

  return (
    <Spin spinning={isloadingWarrantyAdd || isloadingBarcode || isloadingTele}>
      <StyledContainer>
        <TextLarge text="ลงทะเบียนสินค้า" />
        <Row gutter={16}>
          <Col span={14}>
            <CardTheme>
              <TextLarge text="ลูกค้าลงทะเบียนรับประกัน" />
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={FORM_USER_REGISTRATION_WARRANTY({ onEnter: handleOnEnterTele })}
              />
            </CardTheme>
          </Col>
          <Col span={10}>
            <CardTheme>
              <TextLarge text="สินค้าลงทะเบียนรับประกัน" />
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={productLeadingForms}
              />
              {canAddMoreBrandSets && (
                <Button
                  type="dashed"
                  block
                  onClick={() =>
                    setExtraBrandSetCount((c) => Math.min(MAX_EXTRA_BRAND_SETS, c + 1))
                  }
                  style={{ marginTop: 4, marginBottom: 8 }}
                >
                  เพิ่มยี่ห้อและรุ่น
                </Button>
              )}
              {extraBrandFormBlocks.map((block, idx) => (
                <div key={`extra-brand-${idx}`}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => handleRemoveExtraBrandBlock(idx)}
                    >
                      ลบชุดนี้
                    </Button>
                  </div>
                  <RenderForm
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    forms={block}
                  />
                </div>
              ))}
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={productTrailingForms}
              />
            </CardTheme>
          </Col>
        </Row>
        <ContainerButton right>
          <ButtonTheme
            useFor="SUBMIT"
            title="บันทึก"
            style={{ marginTop: '2%' }}
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          />
        </ContainerButton>
      </StyledContainer>
    </Spin>
  );
};
